import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import CourseAPIClient from "../../APIClients/CourseAPIClient";
import { CourseModule } from "../../types/CourseTypes";
import ModuleBookmarksGrid from "./ModuleBookmarksGrid";
import { useCourseUnits } from "../../contexts/CourseUnitsContext";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface ModuleSectionProps {
  module: CourseModule;
  bookmarks: Array<{
    id: string;
    title: string;
    type: string;
    unitId: string;
    moduleId: string;
    pageId: string;
  }>;
  onBookmarkDeleted?: (pageId: string) => void;
  expandAll?: boolean;
  expandAllStamp?: number;
  onToggle?: (isOpen: boolean) => void;
}

const ModuleSection: React.FC<ModuleSectionProps> = ({
  module,
  bookmarks,
  onBookmarkDeleted,
  expandAll = false,
  expandAllStamp,
  onToggle,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [detailedModule, setDetailedModule] = useState<CourseModule | null>(
    null,
  );
  const { moduleDisplayIndex } = useCourseUnits();

  // Sync when expandAll changes OR when a command id changes (button press)
  useEffect(() => {
    // Respond to explicit expand/collapse commands (expandAllCommandId) or real value changes
    setExpanded(Boolean(expandAll));
    // Stamp triggers this effect even if expandAll boolean didn't change
  }, [expandAll, expandAllStamp]);

  // Fetch module details to get lesson PDF URL and populated pages for thumbnails
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const detailed = await CourseAPIClient.getModuleById(module.id);
      if (isMounted && detailed) {
        setDetailedModule(detailed);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [module.id]);

  // Count slides and activities
  const slideCount = bookmarks.filter((b) => b.type === "Lesson").length;
  const activityCount = bookmarks.filter((b) => b.type !== "Lesson").length;

  // Handle pluralization
  const slideLabel = slideCount === 1 ? "slide" : "slides";
  const activityLabel = activityCount === 1 ? "activity" : "activities";

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => {
        setExpanded(isExpanded);
        if (onToggle) onToggle(isExpanded); // notify parent when user manually toggles
      }}
      sx={{
        border: "1px solid",
        borderColor: theme.palette.Neutral[300],
        borderRadius: "8px",
        boxShadow: "none",
      }}
    >
      {/* Header */}
      <AccordionSummary
        expandIcon={
          <ArrowDropDownIcon
            sx={{
              width: "43.192px",
              height: "37.831px",
              color: "#000000",
            }}
          />
        }
        sx={{
          width: "100%",
          backgroundColor: expanded ? "#F5F5F5" : "transparent",
          padding: "30px 32px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
            flex: "1 0 0",
          }}
        >
          <Typography sx={{ ...theme.typography.titleLarge }}>
            Module {moduleDisplayIndex(module.id)}: {module.title}
          </Typography>
          <Typography sx={{ ...theme.typography.labelLarge }}>
            {activityCount} bookmarked {activityLabel}, {slideCount} bookmarked{" "}
            {slideLabel}
          </Typography>
        </Box>
      </AccordionSummary>

      {/* Content */}
      <AccordionDetails
        sx={{
          padding: "32px 32px 42px 32px",
        }}
      >
        <ModuleBookmarksGrid
          module={detailedModule || module}
          bookmarks={bookmarks}
          onBookmarkDeleted={onBookmarkDeleted}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default ModuleSection;
